import { useQuery } from "react-query";
import { supabase } from "../../../config/supabaseClient";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { StaticVariables } from "../../StaticVariables";
import { reactQueryParams } from "../../StayUpToDate/RenderComponent";
import { useCallback, useMemo } from "react";
import { tagTrendingProductsAsProductType } from "../../productImage/productImageDisplay";
import {
  getOrComputeTrendingPostIds,
  type TrendingClickSource,
  type TrendingSnapshotPersist,
} from "./trendingDailySnapshot";
import { resolveTrendingAnalyticsDates } from "../../../Screens/DashboardView/MasterTemplateEditor/websiteEditorTrendingDetails";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Recent Products / Trending Products: LTK, ShopMy, Amazon, MISC only — not IG/TikTok shoppable content. */
function isProductPlatformOnly(platform: unknown): boolean {
  const value = String(platform || "")
    .trim()
    .toLowerCase();
  if (!value) return false;
  const allowed = StaticVariables().PRODUCT_PLATFORMS.map((p: string) =>
    String(p).toLowerCase()
  );
  return allowed.includes(value) || value === "misc-product";
}

function isValidShopLink(shopLink: unknown): boolean {
  return typeof shopLink === "string" && shopLink.trim() !== "";
}

/** Public feed strip size. Custom-order pickers pass a higher cap (up to 100). */
export const TRENDING_MAX_COUNT_DEFAULT = 25;
/** Matches WebsiteEditorPickGrid / custom-order drawer max. */
export const TRENDING_CUSTOM_ORDER_MAX = 100;

export function useLoadTrendingProductsMaxCount(
  user_id: string,
  username: any,
  title: any,
  numberOfClicks: any,
  removedPostIds: any,
  startDate: any,
  endDate: any,
  days: any,
  order: string = "DESC",
  type?: string,
  customOrderPostIds: string[] = [],
  /** When true (e.g. edit modal), returned data includes removed posts so UI can show "Removed" section. When false, removed posts are excluded from the result. */
  includeRemovedInResult: boolean = false,
  /** Optional component row fields so today's snapshot can be read/written once in DB. */
  snapshotPersist?: TrendingSnapshotPersist,
  /** Editor filter changes must recompute. A same-day snapshot keeps the old posts. */
  bypassSnapshot: boolean = false,
  /**
   * How many posts to return after ranking. Public carousels keep the default
   * (25). Custom-order add/edit drawers pass TRENDING_CUSTOM_ORDER_MAX so
   * Load more can page beyond the first batch.
   */
  maxResults: number = TRENDING_MAX_COUNT_DEFAULT,
  /**
   * Website Trending Products passes `template-shop`. Website Trending Content
   * passes `template-content`. Default stays the link-in-bio analytics RPC.
   */
  clickSource: TrendingClickSource = "legacy",
) {
  const getComparableTimestamp = (post: any) => {
    const candidates = [
      post?.post_date,
      post?.postDate,
      post?.published_at,
      post?.created_at,
      post?.createdAt,
      post?.timestamp,
      post?.post_time,
    ];
    for (const value of candidates) {
      if (!value) continue;
      const time = new Date(value).getTime();
      if (!Number.isNaN(time)) {
        return time;
      }
    }
    return 0;
  };

  const applyOrdering = useCallback((items: any[]) => {
    if (!Array.isArray(items)) return [];

    if (order === "CUSTOM" && Array.isArray(customOrderPostIds) && customOrderPostIds.length > 0) {
      const idToItem = new Map<string, any>();
      const baseSorted = [...items].sort((a, b) => getComparableTimestamp(b) - getComparableTimestamp(a));

      baseSorted.forEach((item) => {
        idToItem.set(String(item?.post_id), item);
      });

      const ordered = customOrderPostIds
        .map((id) => idToItem.get(String(id)))
        .filter(Boolean);

      const usedIdSet = new Set(customOrderPostIds.map((id) => String(id)));
      const remaining = baseSorted.filter(
        (item) => !usedIdSet.has(String(item?.post_id))
      );

      return [...ordered, ...remaining];
    }

    if (order === "ASC") {
      return [...items].sort(
        (a, b) => getComparableTimestamp(a) - getComparableTimestamp(b)
      );
    }

    return [...items].sort(
      (a, b) => getComparableTimestamp(b) - getComparableTimestamp(a)
    );
  }, [order, customOrderPostIds]);
  const fetchTrendingProducts = async () => {
    if (removedPostIds && removedPostIds !== "") {
      removedPostIds = Array.from(
        new Set(removedPostIds?.split(",").filter((elem: any) => elem !== ""))
      );
    }
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isNotDateFormat = (dateString: any) => {
      if (dateString == null || dateString === "") return true;
      const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
      return !dateFormatRegex.test(String(dateString).trim());
    };

    /**
     * Preset ranges pass YYYY-MM-DD strings. Custom range historically stored
     * dayjs instances from CustomDateRange — normalize before the regex check
     * so mobile custom picks don't skip the fetch entirely.
     */
    const toYYYYMMDD = (value: any): string => {
      if (value == null || value === "") return "";
      if (typeof value === "string") {
        const t = value.trim();
        return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
      }
      const d = dayjs(value);
      return d.isValid() ? d.tz(userTimezone).format("YYYY-MM-DD") : "";
    };

    startDate = toYYYYMMDD(startDate);
    endDate = toYYYYMMDD(endDate);

    // Preset / All tabs store absolute dates that go stale — always roll to
    // "now" (same as dashboard TrendingProductsWrapper / normalizeTrendingDetails).
    // Custom (activeTab 1 / 14) keeps the stored range.
    const resolved = resolveTrendingAnalyticsDates(
      `${numberOfClicks || 1},${startDate},${endDate},${days ?? 7}`,
    );
    startDate = resolved.startDate;
    endDate = resolved.endDate;

    if (
      startDate &&
      endDate &&
      !isNotDateFormat(startDate) &&
      !isNotDateFormat(endDate)
    ) {
      const { postIds } = await getOrComputeTrendingPostIds({
        params: {
          userId: user_id,
          username,
          numberOfClicks,
          startDate,
          endDate,
          type,
          clickSource,
        },
        persist: snapshotPersist,
        bypassSnapshot,
        label: `TrendingMaxCount:${type || "products"}`,
      });

      if (!postIds.length) {
        return { data: [], total_count: 0 };
      }

      const removedIdsArray = Array.isArray(removedPostIds)
        ? removedPostIds.map((id: any) => String(id).trim()).filter(Boolean)
        : typeof removedPostIds === "string" && removedPostIds
        ? removedPostIds.split(",").map((s: string) => s.trim()).filter(Boolean)
        : [];
      const postIdsForRpc = Array.from(new Set([...postIds, ...removedIdsArray]));

      let result = [];
      let total_count = 0;
      if (type == "trending content") {
        const [{ data: searchResult = [] }] = await Promise.all([
          supabase.rpc("search_postids_content_with_countor", {
            userid: user_id,
            username: username,
            postids: postIdsForRpc,
            socials: StaticVariables().CONTENT_PLATFORMS,
            max_count: 200,
            post_order: order === "CUSTOM" ? "DESC" : order,
            // A saved custom sequence makes Newest/Oldest return that list.
            custom_order: [],
          }),
        ]);
        result = [...(searchResult as any).posts];
        total_count = (searchResult as any).total_count;
      } else {
        const [{ data: searchResult = [] }] = await Promise.all([
          supabase.rpc("searchresulltte_with_countor", {
            userid: user_id,
            username: username,
            postids: postIdsForRpc,
            max_count: 200,
            post_order: order === "CUSTOM" ? "DESC" : order,
            custom_order: [],
          }),
        ]);
        result = [...(searchResult as any).posts];
        total_count = (searchResult as any).total_count;
      }

      let filteredArray = result;

      // Click analytics already scopes by the trending window. Do not also
      // require the post itself to have been published in that window — that
      // drops popular older posts and leaves the carousel empty while add
      // preview (recent content) still shows tiles.
      // Do not filter out removed posts here; the UI splits them into "Added" and "Removed" sections

      // Products only: product platforms + shop_link. Exclude Instagram/TikTok/etc
      // shoppable content (same as useLoadTrendingProducts).
      if (type?.toLowerCase()?.trim() !== "trending content") {
        filteredArray = filteredArray.filter(
          (obj: any) =>
            isValidShopLink(obj?.shop_link) &&
            isProductPlatformOnly(obj?.platform)
        );
      }

      filteredArray = Array.from(
        new Map(
          filteredArray.map((post: any) => [post.post_id, post])
        ).values()
      );

      const orderedArray = applyOrdering(filteredArray);
      // Same PRODUCT tagging as useLoadTrendingProducts (dashboard carousel + edit modal).
      const taggedArray = tagTrendingProductsAsProductType(orderedArray, type);

      // By default exclude removed posts from the result (dashboard/frontend). Edit modal passes includeRemovedInResult=true to get both for "Added" and "Removed" sections.
      const removedIdsList =
        !includeRemovedInResult && removedPostIds
          ? Array.isArray(removedPostIds)
            ? removedPostIds.map((id: any) => String(id).trim()).filter(Boolean)
            : String(removedPostIds).split(",").map((s: string) => s.trim()).filter(Boolean)
          : [];
      const removedSet = removedIdsList.length > 0 ? new Set(removedIdsList) : null;
      const finalArray =
        removedSet
          ? taggedArray.filter((post: any) => !removedSet.has(String(post?.post_id)))
          : taggedArray;

      const limit =
        Number.isFinite(maxResults) && maxResults > 0
          ? Math.floor(maxResults)
          : TRENDING_MAX_COUNT_DEFAULT;

      return {
        data: finalArray.slice(0, limit),
        total_count: finalArray.length,
      };
    }
  };
  // Serialize removedPostIds for query key to ensure React Query detects changes
  const removedPostIdsKey = useMemo(() => {
    if (!removedPostIds || removedPostIds === "") {
      return "";
    }
    return removedPostIds
      .split(",")
      .map((id: any) => String(id).trim())
      .filter((id: string) => id !== "")
      .sort() // Sort to ensure consistent key regardless of order
      .join(",");
  }, [removedPostIds]);

  const snapshotPersistKey = useMemo(() => {
    if (!snapshotPersist?.componentId) return "";
    return `${snapshotPersist.componentId}|${snapshotPersist.from || ""}|${snapshotPersist.query || ""}|${snapshotPersist.postIds || ""}`;
  }, [
    snapshotPersist?.componentId,
    snapshotPersist?.from,
    snapshotPersist?.query,
    snapshotPersist?.postIds,
  ]);

  const customOrderKey = useMemo(() => {
    if (!Array.isArray(customOrderPostIds) || customOrderPostIds.length === 0) {
      return "";
    }
    return customOrderPostIds.map((id) => String(id)).filter(Boolean).join(",");
  }, [customOrderPostIds]);

  const queryResult = useQuery(
    [
      "TrendingProductsMaxCount",
      user_id,
      numberOfClicks,
      removedPostIdsKey,
      startDate,
      endDate,
      days,
      type,
      // Keep CUSTOM distinct from DESC (same tile set, different sort). Do not
      // put the id list in the key — live reorder is applied below.
      order === "CUSTOM" ? "CUSTOM" : order,
      includeRemovedInResult,
      snapshotPersistKey,
      bypassSnapshot ? "live" : "snap",
      Number.isFinite(maxResults) && maxResults > 0
        ? Math.floor(maxResults)
        : TRENDING_MAX_COUNT_DEFAULT,
      clickSource,
    ],
    fetchTrendingProducts,
    {
      enabled:
        Boolean(title) &&
        Boolean(numberOfClicks) &&
        Boolean(
          Array.from(
            new Set(
              removedPostIds
                ?.split(",")
                .filter((elem: any) => elem.trim() !== "")
            )
          ).length >= 0
        ),
      ...reactQueryParams(),
      // Editor live filters must not keep a day-long stale tile set.
      staleTime: bypassSnapshot ? 0 : 24 * 60 * 60 * 1000,
      cacheTime: bypassSnapshot ? 5 * 60 * 1000 : 24 * 60 * 60 * 1000,
    }
  );

  const data = useMemo(() => {
    const raw = queryResult.data;
    if (!raw || !Array.isArray(raw.data)) return raw;
    if (order !== "CUSTOM" || !customOrderKey) return raw;
    return { ...raw, data: applyOrdering(raw.data) };
  }, [queryResult.data, order, customOrderKey, applyOrdering]);

  return { ...queryResult, data };
}
